/**
 * ================================================================
 * VERSION CHECK SYSTEM
 * ================================================================
 * 
 * Tự động kiểm tra version mới và reload app khi có update
 * 
 * CÁCH DÙNG:
 * 1. Khi deploy version mới, tăng version trong public/version.json
 * 2. User vào app sẽ tự động kiểm tra version
 * 3. Nếu khác version cũ → Hiển thị popup và tự động reload
 * 
 * FEATURES:
 * - Kiểm tra version theo interval (default: 5 phút)
 * - Lưu version hiện tại vào localStorage
 * - Popup thông báo trước khi reload
 * - Auto reload sau vài giây
 * - Force reload để clear cache
 */

import Swal from 'sweetalert2';

// Config
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 phút
const VERSION_FILE_URL = '/version.json'; // File chứa version mới
const STORAGE_KEY = 'app_version';
const AUTO_RELOAD_DELAY = 5000; // 5 giây

/**
 * Get current version from localStorage
 */
function getCurrentVersion() {
  return localStorage.getItem(STORAGE_KEY);
}

/**
 * Set current version to localStorage
 */
function setCurrentVersion(version) {
  localStorage.setItem(STORAGE_KEY, version);
  console.log(`[VersionCheck] Version set to: ${version}`);
}

/**
 * Fetch latest version from server
 */
async function fetchLatestVersion() {
  try {
    // Add timestamp to prevent cache
    const timestamp = new Date().getTime();
    const response = await fetch(`${VERSION_FILE_URL}?t=${timestamp}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    return data.version;
  } catch (error) {
    console.error('[VersionCheck] Failed to fetch version:', error);
    return null;
  }
}

/**
 * Show update notification and reload
 */
function showUpdateNotification(currentVersion, latestVersion) {
  console.log(`[VersionCheck] New version available: ${currentVersion} → ${latestVersion}`);
  
  Swal.fire({
    title: '🎉 Có phiên bản mới!',
    html: `
      <p>Đã có phiên bản mới của hệ thống.</p>
      <p><strong>Phiên bản hiện tại:</strong> ${currentVersion || 'Chưa xác định'}</p>
      <p><strong>Phiên bản mới:</strong> ${latestVersion}</p>
      <p class="text-muted" style="margin-top: 15px; font-size: 14px;">
        Trang sẽ tự động tải lại sau <span id="countdown">5</span> giây...
      </p>
    `,
    icon: 'info',
    showCancelButton: true,
    confirmButtonText: 'Tải lại ngay',
    cancelButtonText: 'Để sau',
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    allowOutsideClick: false,
    timer: AUTO_RELOAD_DELAY,
    timerProgressBar: true,
    didOpen: () => {
      // Countdown timer
      const countdownElement = document.getElementById('countdown');
      let secondsLeft = 5;
      
      const countdownInterval = setInterval(() => {
        secondsLeft--;
        if (countdownElement) {
          countdownElement.textContent = secondsLeft;
        }
        if (secondsLeft <= 0) {
          clearInterval(countdownInterval);
        }
      }, 1000);
    }
  }).then((result) => {
    if (result.isConfirmed || result.dismiss === Swal.DismissReason.timer) {
      // User clicked "Tải lại ngay" hoặc timeout
      reloadApp(latestVersion);
    } else {
      // User clicked "Để sau"
      console.log('[VersionCheck] User postponed update');
      // Vẫn lưu version mới để lần sau không hỏi nữa
      setCurrentVersion(latestVersion);
    }
  });
}

/**
 * Reload app and clear cache
 */
function reloadApp(newVersion) {
  console.log(`[VersionCheck] Reloading app to version ${newVersion}...`);
  
  // Update version trước khi reload
  setCurrentVersion(newVersion);
  
  // Clear cache và reload
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }
  
  // Force reload (bypass cache)
  window.location.reload(true);
}

/**
 * Check for new version
 */
async function checkVersion() {
  const currentVersion = getCurrentVersion();
  const latestVersion = await fetchLatestVersion();
  
  if (!latestVersion) {
    console.warn('[VersionCheck] Could not fetch latest version');
    return;
  }
  
  console.log('[VersionCheck] Current:', currentVersion, '| Latest:', latestVersion);
  
  // Lần đầu vào app (chưa có version)
  if (!currentVersion) {
    console.log('[VersionCheck] First time, setting version:', latestVersion);
    setCurrentVersion(latestVersion);
    return;
  }
  
  // So sánh version
  if (currentVersion !== latestVersion) {
    showUpdateNotification(currentVersion, latestVersion);
  } else {
    console.log('[VersionCheck] Already on latest version');
  }
}

/**
 * Start version check polling
 */
let versionCheckInterval = null;

export function startVersionCheck() {
  console.log(`[VersionCheck] Started (interval: ${VERSION_CHECK_INTERVAL / 1000}s)`);
  
  // Check ngay lần đầu
  checkVersion();
  
  // Check định kỳ
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval);
  }
  
  versionCheckInterval = setInterval(() => {
    checkVersion();
  }, VERSION_CHECK_INTERVAL);
}

/**
 * Stop version check polling
 */
export function stopVersionCheck() {
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval);
    versionCheckInterval = null;
    console.log('[VersionCheck] Stopped');
  }
}

/**
 * Manual version check (for testing)
 */
export function checkVersionNow() {
  console.log('[VersionCheck] Manual check triggered');
  return checkVersion();
}

/**
 * Reset version (for testing)
 */
export function resetVersion() {
  localStorage.removeItem(STORAGE_KEY);
  console.log('[VersionCheck] Version reset');
}

/**
 * Get version info
 */
export function getVersionInfo() {
  return {
    current: getCurrentVersion(),
    storageKey: STORAGE_KEY,
    checkInterval: VERSION_CHECK_INTERVAL
  };
}
